import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Grid } from '@mui/material'
import { isArray } from 'lodash';
import React, { useEffect } from 'react'
import { FieldValues, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useRecoilState } from 'recoil';
import { StaticDataService } from '../../../Component/Api/StaticData/StaticDataService';
import { VerificationAgencyClientService } from '../../../Component/Api/VerificationAgency/VerificationAgencyClient/VerificationAgencyClientService';
import AppAutoComplete from '../../../Component/Common/Components/FormValidationInputs/AppAutoComplete';
import AppSelectList from '../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import AppTextInput from '../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import { MDTypography } from '../../../Component/Common/Components/MDTypography'
import { companyTypeList } from '../../../Component/Common/Constants/Lists/DropdownListOptions';
import { AddressResponse } from '../../../Component/Model/Dto/Address';
import { Province, Suburb } from '../../../Component/Model/Dto/StaticData';
import { VerificationAgencyClient } from '../../../Component/Model/Dto/VerificationAgency/VerificationAgencyClient';
import { MapAddressDtoOutToIn } from '../../../Component/Model/helpers';
import { VerificationCompanyAtom } from '../../../State/VerificationCompany.Atom';
import { CompanyDetailsValidationSchema } from './CompanyDetailsValidationSchema';

interface ICompanyDetailsApplication {
    token: any;
    handleNextCount: number;
    setHandleNextCount: (i: number) => void;
}

export const CompanyDetailsExternalApplicationPage = (props: ICompanyDetailsApplication) => {
    const [loading, setLoading] = React.useState(false);
    const [suburbs, setSuburbs] = React.useState<Suburb[]>([]);
    const [provinces, setProvinces] = React.useState<Province[]>([]);
    const [company, setCompany] = useRecoilState(VerificationCompanyAtom);

    const { control, handleSubmit, formState: { isSubmitting }, reset } = useForm({
        mode: 'all',
        resolver: yupResolver(CompanyDetailsValidationSchema),
    });

    const handleDataSubmit = async (data: FieldValues) => {
        props.setHandleNextCount(props.handleNextCount + 1);
        try {
            const address = {
                AddressLine1: data.addressLine1,
                AddressLine2: data.addressLine2,
                Province: data.province,
                Suburb: data.suburb,
                Code: data.code,
            } as AddressResponse;

            const mappedAddress = MapAddressDtoOutToIn(address);

            const requestData = {
                vaClientId: company.vaClientId,
                companyName: data.companyName,
                companyTypeId: data.companyTypeId,
                address: mappedAddress,
                referenceNumber: data.referenceNumber,
                registrationNumber: data.registrationNumber,
                tradingAs: data.tradingAs,
                vatNumber: data.vatNumber,
                token: props.token
            } as VerificationAgencyClient.CompanyDetailsTempAccessUpdateDtoIn;

            const response = await VerificationAgencyClientService.UpdateEntryExternalPage(requestData);

            toast.success("Company Details Saved");
        } catch (error: any) {
            console.log(error);
            if (isArray(error)) {
                let Errors: string[] = error;
                Errors.forEach((error: string) => {
                    toast.error(error);
                });
            }
        }
    }

    const getSuburbs = async () => {
        try {
            const suburbsResponse = await StaticDataService.getSuburbs();
            setSuburbs(suburbsResponse.data);
        } catch (error) {
            console.log(error);
        }
    }

    const getProvinces = async () => {
        try {
            const provincesResponse = await StaticDataService.getProvinces();
            setProvinces(provincesResponse.data);
        } catch (error) {
            console.log(error);
        }
    }

    const getCompanyDetails = async () => {
        if (props.token !== null) {
            try {
                const response = await VerificationAgencyClientService.GetCompanyDetailsForExternalApplication(props.token);

                const formData = {
                    referenceNumber: response.data.referenceNumber,
                    companyName: response.data.companyName,
                    tradingAs: response.data.tradingAs,
                    vatNumber: response.data.vatNumber,
                    companyTypeId: response.data.companyTypeId,
                    registrationNumber: response.data.registrationNumber,
                    addressLine1: response.data.address?.addressLine1,
                    addressLine2: response.data.address?.addressLine2,
                    suburb: response.data.address?.suburb_Suburb ?
                        {
                            Id: response.data.address?.suburb_Suburb?.id,
                            Name: response.data.address?.suburb_Suburb?.name
                        } : null,
                    province: response.data.address?.province_Province ?
                        {
                            Id: response.data.address?.province_Province?.id,
                            Name: response.data.address?.province_Province?.name
                        } : null,
                    code: response.data.address?.code
                };
                reset(formData);
            } catch (error) {
                console.log(error);
            }
        }
    }

    useEffect(() => {
        setLoading(true);
        getSuburbs();
        getProvinces();
        getCompanyDetails();
        setLoading(false);
    }, [])


    return (
        <Box>
            <MDTypography color='#000000' variant='h5' fontWeight='bold' >
                Company Details
            </MDTypography>
            <MDTypography sx={{ fontSize: "15px", paddingBottom: "8px", paddingTop: "8px", color: "#727272" }}>
                Accurate business details, such as registration number, VAT number, and location, are essential for BEE compliance and verification.
            </MDTypography>
            <form id='CompanyDetailsExternalForm' onSubmit={handleSubmit(handleDataSubmit)}>
                <MDTypography color='primary' fontSize="14px" fontWeight='bold' paddingBottom='10px' >
                    GENERAL
                </MDTypography>
                <Grid container spacing={3}>
                    <Grid item xs={6} sm={6}>
                        <Box>
                            <AppSelectList control={control} name="companyTypeId" label="Company Type" items={companyTypeList} size={"small"} />
                        </Box>
                    </Grid>
                    <Grid item xs={6} sm={6}>
                        <Box>
                            <AppTextInput control={control} name="referenceNumber" label="Reference Number (Internal)" size={"small"} disabled />
                        </Box>
                    </Grid>
                    <Grid item xs={6} sm={6}>
                        <Box>
                            <AppTextInput control={control} name="companyName" label="Company Name" size={"small"} />
                        </Box>
                    </Grid>
                    <Grid item xs={6} sm={6}>
                        <Box>
                            <AppTextInput control={control} name="tradingAs" label="Trading As" size={"small"} />
                        </Box>
                    </Grid>
                    <Grid item xs={6} sm={6}>
                        <Box>
                            <AppTextInput control={control} name="registrationNumber" label="Registration Number" size={"small"} />
                        </Box>
                    </Grid>
                    <Grid item xs={6} sm={6}>
                        <Box>
                            <AppTextInput control={control} name="vatNumber" label="VAT Number" size={"small"} />
                        </Box>
                    </Grid>
                    <MDTypography color='primary' fontSize="14px" fontWeight='bold' paddingBottom='10px' marginTop='10px' marginLeft='23px'>
                        LOCATION
                    </MDTypography>
                    <Grid container spacing={3} marginLeft='1px'>
                        <Grid item xs={6} sm={6}>
                            <Box>
                                <AppTextInput control={control} name="addressLine1" label="Address Line 1" size={"small"} />
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <Box>
                                <AppTextInput control={control} name="addressLine2" label="Address Line 2" size={"small"} />
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <Box>
                                <AppAutoComplete control={control} name="suburb" label="Suburb" options={suburbs} size={"small"} loading={loading} />
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <Box>
                                <AppAutoComplete control={control} name="province" label="Province" options={provinces} size={"small"} loading={loading} />
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <Box>
                                <AppTextInput control={control} name="code" label="Code" size={"small"} />
                            </Box>
                        </Grid>
                    </Grid>
                    <Box mt={2} width='100%' display='flex' justifyContent='space-between'>
                        <Box></Box>
                        <LoadingButton loading={isSubmitting} variant='contained' form='CompanyDetailsExternalForm' type="submit" color='primary'>
                            Next
                        </LoadingButton>
                    </Box>
                </Grid>
            </form>
        </Box>
    )
}
